import { ReactNode } from 'react';
import { Validators } from 'utils/validators';
import { FormModel } from 'components/Form';

const section = (field: string, label: string): FormModel => ({
  label,
  value: '',
  type: 'section',
  placeholder: '',
  field,
  validators: [],
  required: false,
  responsive: { xs: 12 },
  compactSection: true,
});

const custom = (field: string, children: ReactNode, responsive: FormModel['responsive']): FormModel => ({
  label: '',
  value: '',
  type: 'custom',
  placeholder: '',
  field,
  validators: [],
  required: false,
  responsive,
  children,
});

export const ActualRevenueForm = (
  strings: any,
  isWithinActualizationWindow: boolean,
  isTransferToRoom: boolean,
  serviceChargeDropdown: any,
  serviceChargeList: any,
  hasSplitRevenuePrivilege: boolean,
  revenueOwnerList: any[],
  bookedServiceChargePercent: number,
  excessGuestCount: number = 0,
  extraGuestRateText: string = '',
  venueChargesTable: ReactNode = null,
  tdsField: ReactNode = null,
  roundOffField: ReactNode = null,
): FormModel[] => {
  const eventFieldResponsive = { xs: 12, sm: 6, md: 3 };
  const revenueOwnersResponsive = { xs: 12, sm: 8, md: 9 };
  const roundOffResponsive = { xs: 12, sm: 4, md: 3 };
  const excessGuestRateField: FormModel = {
    label: strings.booking_details_actual_revenue_excess_guest_rate,
    value: '',
    size: 'medium',
    type: 'text',
    typeValue: 'number',
    variant: 'outlined',
    disabled: !isWithinActualizationWindow,
    placeholder: '',
    field: 'excessGuestRate',
    validators: [{ check: Validators.validateFloat, min: 0, max: 100 }],
    responsive: eventFieldResponsive,
    sx: { pr: 2 },
    required: false,
    endAdornment: '%',
    belowText: extraGuestRateText
      ? String(strings.formatString(strings.actualization_excess_guest_rate_hint, extraGuestRateText))
      : '',
    belowTextRight:
      excessGuestCount > 0
        ? String(strings.formatString(strings.actualization_excess_guest_count_chip, excessGuestCount))
        : strings.actualization_no_change,
    belowTextRightTone: excessGuestCount > 0 ? 'positive' : 'neutral',
  };

  /** Not rendered; values are edited from the Venue Charges table. */
  const venueFields: FormModel[] = [
    { field: 'guaranteedGuests', label: strings.booking_details_actual_revenue_number_of_guest, required: true },
    { field: 'billableGuests', label: '', required: false },
    { field: 'hallRental', label: '', required: false },
  ].map(({ field, label, required }) => ({
    label,
    value: '',
    type: 'hidden',
    placeholder: '',
    field,
    validators: required ? [{ check: Validators.validateMandatoryField }] : [],
    required,
    responsive: { xs: 12 },
  }));

  const formFields: FormModel[] = [
    section('event_details_section', strings.actualization_event_details),
    {
      label: strings.bookingHistoryTabsMinimumRevenue,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'number',
      disabled: true,
      variant: 'outlined',
      placeholder: '',
      field: 'minimumRevenue',
      validators: [{ check: Validators.validateFloat }],
      responsive: eventFieldResponsive,
      sx: { pr: 2 },
      required: false,
      endChip: strings.actualization_billable_calculated,
    },
    {
      label: strings.new_booking_service_charge,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'number',
      disabled: !isWithinActualizationWindow,
      variant: 'outlined',
      placeholder: '',
      field: 'serviceChargePercentage',
      validators: [{ check: Validators.validateFloat, min: 0, max: 100 }],
      responsive: eventFieldResponsive,
      sx: { pr: 2 },
      required: false,
      endAdornment: '%',
      bookedBaseline: bookedServiceChargePercent,
      bookedVarianceFormat: 'percent',
    },
    excessGuestRateField,
    ...(tdsField ? [custom('tds_field', tdsField, eventFieldResponsive)] : []),
    {
      label: strings.revenue_owner_title_list,
      value: [],
      size: 'medium',
      type: 'select',
      typeValue: 'text',
      isMulti: true,
      placeholder: '',
      field: 'revenueOwners',
      validators: [],
      required: false,
      responsive: revenueOwnersResponsive,
      sx: { pr: 2 },
      options: revenueOwnerList,
      disabled: !isWithinActualizationWindow,
    },
    ...(roundOffField ? [custom('round_off_field', roundOffField, roundOffResponsive)] : []),
    section('settlement_section', strings.actualization_settlement),
    {
      label: strings.actualization_isTransferToRoom,
      value: false,
      size: 'medium',
      selected: isTransferToRoom,
      type: 'checkbox',
      field: 'isTransferToRoom',
      disabled: !isWithinActualizationWindow,
      responsive: { xs: 12, sm: 6, md: 3 },
      placeholder: '',
      validators: [],
      required: false,
    },
  ];

  if (isTransferToRoom) {
    formFields.push(
      {
        label: strings.actualization_roomNumber,
        value: '',
        size: 'medium',
        type: 'text',
        typeValue: 'text',
        variant: 'outlined',
        disabled: !isWithinActualizationWindow,
        placeholder: '',
        field: 'roomNumber',
        validators: [{ check: Validators.validateMandatoryField }, { check: Validators.validateString, max: 50 }],
        responsive: { xs: 12, sm: 6, md: 3 },
        required: true,
        sx: { pr: 2 },
      },
      {
        label: strings.actualization_reservation_name,
        value: '',
        size: 'medium',
        type: 'text',
        typeValue: 'text',
        variant: 'outlined',
        placeholder: '',
        field: 'roomReservationName',
        validators: [],
        responsive: { xs: 12, sm: 6, md: 3 },
        required: false,
        disabled: true,
        sx: { pr: 2 },
      },
      {
        label: strings.actualization_reservation_id,
        value: '',
        size: 'medium',
        type: 'text',
        typeValue: 'text',
        variant: 'outlined',
        placeholder: '',
        field: 'roomReservationId',
        validators: [],
        responsive: { xs: 12, sm: 6, md: 3 },
        required: false,
        disabled: true,
        sx: { pr: 2 },
      },
    );
  }

  if (serviceChargeDropdown) {
    const idx = formFields.findIndex((i: any) => i.field === 'serviceChargePercentage');
    if (idx >= 0) {
      formFields[idx] = {
        label: strings.new_booking_service_charge,
        value: {},
        size: 'medium',
        type: 'drop-down',
        placeholder: `${strings.new_booking_service_charge} `,
        field: 'serviceCharge',
        validators: [],
        responsive: eventFieldResponsive,
        required: false,
        options: serviceChargeList,
        sx: { mr: 2 },
        bookedBaseline: bookedServiceChargePercent,
        bookedVarianceFormat: 'percent',
      };
    }
  }

  formFields.push(section('venue_charges_section', strings.actualization_venue_charges));
  formFields.push(...venueFields);
  if (venueChargesTable) {
    formFields.push(custom('venue_charges_table', venueChargesTable, { xs: 12 }));
  }

  return formFields;
};
